import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { useCallback, useEffect, useRef } from "react";

import { useShouldSampleSignupTracking } from "@/react/components/OnboardingPage/hooks/useShouldSampleSignupTracking";
import type { OtpChallengeFlow } from "@/react/contexts/OtpChallengeContext";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";

export const useOtpChallengeTracker = (flow?: OtpChallengeFlow) => {
  const { trackEvent } = useEventsTracker();
  const shouldSample = useShouldSampleSignupTracking();
  const { currentCommunity } = useCurrentCommunityContext();
  const isOtpFirstCommunityAuth = Boolean(
    currentCommunity?.growth_otp_first_member_auth_enabled,
  );
  const isViewedTrackedRef = useRef(false);

  const trackViewed = useCallback(() => {
    if (!shouldSample) return;
    if (isViewedTrackedRef.current) return;
    isViewedTrackedRef.current = true;

    trackEvent(
      "otp_challenge.viewed",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackViewedRef = useRef(trackViewed);
  trackViewedRef.current = trackViewed;

  useEffect(() => {
    trackViewedRef.current();
  }, []);

  const trackCodeSubmitted = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.code_submitted",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackSucceeded = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.succeeded",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackFailed = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.failed",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackResendClicked = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.resend_clicked",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackSignInWithPasswordClicked = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.sign_in_with_password_clicked",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  const trackSkipClicked = useCallback(() => {
    if (!shouldSample) return;

    trackEvent(
      "otp_challenge.skip_clicked",
      { is_otp_first_community_auth: isOtpFirstCommunityAuth, flow },
      { batch: false },
    );
  }, [shouldSample, trackEvent, isOtpFirstCommunityAuth, flow]);

  return {
    trackCodeSubmitted,
    trackSucceeded,
    trackFailed,
    trackResendClicked,
    trackSignInWithPasswordClicked,
    trackSkipClicked,
  };
};
